/**
 * Slugify Utility
 * 
 * Converts a text string into a URL-friendly slug with proper handling of Portuguese accents.
 * Used for generating consistent, predictable IDs across the application.
 * 
 * @example
 * slugify('Madeira de Pinho')         // 'madeira_de_pinho'
 * slugify('Gestão Florestal')         // 'gestao_florestal'
 * slugify('Preços e Mercado')         // 'precos_e_mercado'
 * slugify('Hello World', 10)          // 'hello_worl'
 */

/**
 * Convert a string to a lowercase, underscore-separated slug.
 * 
 * Features:
 * - Normalizes Unicode characters (NFD) and strips diacritical marks
 * - Handles Portuguese accents (á, é, í, ó, ú, ã, õ, ç, etc.)
 * - Converts spaces and special characters to underscores
 * - Removes leading/trailing underscores
 * - Collapses multiple underscores into one
 * - Optional maximum length truncation
 * 
 * @param text - The text to slugify
 * @param maxLength - Maximum length of the resulting slug (default: 160)
 * @returns A slugified string suitable for use as an ID
 */
export function slugify(text: string, maxLength: number = 160): string {
  return text
    .normalize('NFD')                    // Decompose Unicode (e.g., á → a + ́)
    .replace(/[\u0300-\u036f]/g, '')     // Strip diacritical marks
    .toLowerCase()                        // Convert to lowercase
    .replace(/[^a-z0-9]+/g, '_')         // Replace non-alphanumeric with underscores
    .replace(/^_+|_+$/g, '')             // Remove leading/trailing underscores
    .replace(/_+/g, '_')                 // Collapse multiple underscores
    .slice(0, maxLength);                // Truncate to max length
}

/**
 * Generate a unique slug by appending a number if the base slug already exists.
 * 
 * @param baseSlug - The base slug to make unique
 * @param existingCheck - Async function that checks if a slug exists
 * @returns A unique slug
 * 
 * @example
 * await getUniqueSlug('product', async (slug) => {
 *   const doc = await db.collection('products').doc(slug).get();
 *   return doc.exists;
 * })
 * // Returns: 'product' if available, 'product_1', 'product_2', etc. if not
 */
export async function getUniqueSlug(
  baseSlug: string,
  existingCheck: (slug: string) => Promise<boolean>
): Promise<string> {
  let slug = baseSlug;
  let counter = 1;

  while (await existingCheck(slug)) {
    slug = `${baseSlug}_${counter}`;
    counter++;
  }

  return slug;
}
